import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Slide,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LoopIcon from '@mui/icons-material/Loop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  NodeSelect,
  NodeMentionEditor,
  NodeSection,
  NodeRadioGroup,
  NodeAddButton,
} from '../../../NodeUI';
import { NODE_UI, inputSx, stopPropagation } from '../../../NodeUI/nodeUiStyles';
import { send_request } from '../../../../utils/Request';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FUNCTION_OPTIONS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'greater than or equal to' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'less than or equal to' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
];

const LOGIC_OPTIONS = [
  { value: 'and', label: 'AND' },
  { value: 'or', label: 'OR' },
];

const noValueOperators = new Set(['empty', 'not_empty']);

const createEmptyCondition = (combinator = undefined) => ({
  field: '',
  operator: 'eq',
  value: '',
  ...(combinator ? { combinator } : {}),
});

const ConditionRow = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  padding: NODE_UI.spacing.md,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  position: 'relative',
  '&:hover .condition-actions': { opacity: 1 },
}));

const ActionsRow = styled('div')({
  position: 'absolute',
  top: 6,
  right: 6,
  display: 'flex',
  gap: 2,
  opacity: 0,
  transition: 'opacity 0.15s',
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: 3,
  borderRadius: NODE_UI.borderRadiusSm,
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.error.main },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({ ...inputSx(theme) }));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

/**
 * Dialog for configuring loop edge settings including max iterations and exit conditions.
 */
function LoopConfigDialog({
  isOpen,
  onClose,
  onConfirm,
  initialMaxIterations = 10,
  initialExitCondition = null,
  isEditMode = false,
  projectId,
  sourceComponentId,
}) {
  const [maxIterations, setMaxIterations] = useState(initialMaxIterations);
  const [error, setError] = useState('');
  const [exitConditionEnabled, setExitConditionEnabled] = useState(false);
  const [conditions, setConditions] = useState([createEmptyCondition()]);
  const [availablePlaceholders, setAvailablePlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);

  useEffect(() => {
    const fetchPlaceholders = async () => {
      if (!projectId || !sourceComponentId) return;
      setLoadingPlaceholders(true);
      try {
        const url = `component-service/placeholder/${projectId}/${sourceComponentId}`;
        const response = await send_request(url, '', '', 'get');
        if (response?.data?.placeholders) {
          setAvailablePlaceholders(response.data.placeholders);
        }
      } catch (err) {
        // Silently fail
      } finally {
        setLoadingPlaceholders(false);
      }
    };
    if (isOpen) fetchPlaceholders();
  }, [isOpen, projectId, sourceComponentId]);

  useEffect(() => {
    if (isOpen) {
      setMaxIterations(initialMaxIterations);
      setError('');
      if (initialExitCondition && initialExitCondition.enabled) {
        setExitConditionEnabled(true);
        setConditions(initialExitCondition.rules || initialExitCondition.conditions || [createEmptyCondition()]);
      } else {
        setExitConditionEnabled(false);
        setConditions([createEmptyCondition()]);
      }
    }
  }, [isOpen, initialMaxIterations, initialExitCondition]);

  const handleIterationsChange = (event) => {
    const value = event.target.value;
    if (value === '') { setMaxIterations(''); setError(''); return; }
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) { setError('Please enter a valid number'); return; }
    if (numValue < 1) { setError('Minimum is 1 iteration'); setMaxIterations(numValue); return; }
    if (numValue > 1000) { setError('Maximum is 1000 iterations'); setMaxIterations(numValue); return; }
    setMaxIterations(numValue);
    setError('');
  };

  const updateCondition = useCallback((index, field, value) => {
    setConditions(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const addCondition = useCallback(() => {
    setConditions(prev => [...prev, createEmptyCondition('and')]);
  }, []);

  const removeCondition = useCallback((index) => {
    setConditions(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      if (next.length > 0) {
        const { combinator, ...rest } = next[0];
        next[0] = rest;
      }
      return next;
    });
  }, []);

  const handleConfirm = () => {
    const finalIterations = maxIterations === '' ? 10 : maxIterations;
    if (finalIterations >= 1 && finalIterations <= 1000) {
      const exitCondition = exitConditionEnabled ? {
        enabled: true,
        rules: conditions.filter(c => c.field.trim() !== ''),
      } : null;
      onConfirm(finalIterations, exitCondition);
    }
  };

  const isIterationsValid = maxIterations !== '' && maxIterations >= 1 && maxIterations <= 1000;
  const isExitConditionValid = !exitConditionEnabled ||
    conditions.some(c => c.field.trim() !== '');
  const isValid = isIterationsValid && isExitConditionValid;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LoopIcon sx={{ color: 'text.secondary' }} />
          <span>{isEditMode ? 'Edit Loop' : 'Configure Loop'}</span>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: NODE_UI.spacing.lg }}>
        <Typography variant="body2" color="text.secondary">
          {isEditMode
            ? 'Update the loop settings.'
            : 'This connection creates a loop. Configure when the loop should exit.'}
        </Typography>

        <div>
          <Label>Maximum Iterations</Label>
          <StyledTextField
            autoFocus
            type="number"
            fullWidth
            size="small"
            value={maxIterations}
            onChange={handleIterationsChange}
            error={!!error}
            helperText={error || 'The loop will run at most this many times (1-1000)'}
            inputProps={{ min: 1, max: 1000 }}
          />
        </div>

        <FormControlLabel
          control={
            <Checkbox
              checked={exitConditionEnabled}
              onChange={(e) => setExitConditionEnabled(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography sx={{ fontSize: NODE_UI.fontSize.value, fontWeight: NODE_UI.fontWeight.label }}>
              Exit early when condition is met
            </Typography>
          }
        />

        {exitConditionEnabled && (
          <NodeSection title="Exit Conditions">
            {loadingPlaceholders ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                  Loading placeholders...
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                Type <strong>$</strong> to select a placeholder
              </Typography>
            )}

            {conditions.map((condition, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <NodeRadioGroup
                    value={condition.combinator || 'and'}
                    onChange={(val) => updateCondition(index, 'combinator', val)}
                    options={LOGIC_OPTIONS}
                    row
                  />
                )}

                <ConditionRow>
                  <ActionsRow className="condition-actions">
                    {conditions.length > 1 && (
                      <ActionButton
                        size="small"
                        onClick={(e) => { stopPropagation(e); removeCondition(index); }}
                        onMouseDown={stopPropagation}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </ActionButton>
                    )}
                  </ActionsRow>

                  <NodeMentionEditor
                    label="Value"
                    value={condition.field || ''}
                    onChange={(val) => updateCondition(index, 'field', val)}
                    availablePlaceholders={availablePlaceholders}
                    placeholder="$ for placeholder"
                    variant="minimal"
                  />

                  <NodeSelect
                    label="Condition"
                    value={condition.operator || 'eq'}
                    onChange={(val) => updateCondition(index, 'operator', val)}
                    options={FUNCTION_OPTIONS}
                  />

                  {!noValueOperators.has(condition.operator) && (
                    <NodeMentionEditor
                      label="Compare to"
                      value={condition.value || ''}
                      onChange={(val) => updateCondition(index, 'value', val)}
                      availablePlaceholders={availablePlaceholders}
                      placeholder="Value or $ for placeholder"
                      variant="minimal"
                    />
                  )}
                </ConditionRow>
              </React.Fragment>
            ))}

            <NodeAddButton label="Add Condition" onClick={addCondition} />
          </NodeSection>
        )}

        {isValid && (
          <Box
            sx={{
              p: NODE_UI.spacing.md,
              borderRadius: NODE_UI.borderRadius,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
              <Box>
                <Typography sx={{ fontSize: NODE_UI.fontSize.value, fontWeight: NODE_UI.fontWeight.label, color: 'text.secondary' }}>
                  {exitConditionEnabled && conditions.some(c => c.field.trim() !== '')
                    ? `Exit when condition is met, or after ${maxIterations} iteration${maxIterations !== 1 ? 's' : ''}`
                    : `This loop will run up to ${maxIterations} time${maxIterations !== 1 ? 's' : ''}`
                  }
                </Typography>
                <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                  {exitConditionEnabled
                    ? 'When the loop exits, flow continues to downstream components.'
                    : 'Each iteration will overwrite the previous output values.'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" size="small" disabled={!isValid}>
          {isEditMode ? 'Save Changes' : 'Create Loop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LoopConfigDialog;
